import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Mic,
  FileText,
  ArrowRight,
  PoundSterling,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/quote-from-description";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Quote from a Description: AI Quote Generator for UK Builders",
  "description": "Describe the job in plain English, by typing or using your voice, and get a fully-priced construction quote in minutes. Built for UK builders.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "AI Quoting Software", "item": "https://thepropertool.com/ai-quoting-software" },
    { "@type": "ListItem", "position": 3, "name": "Quote from a Description", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Can AI write a construction quote from a description?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool's AI reads your plain-English description of the job and turns it into a fully-priced, itemised construction quote. You describe the scope (trades, elements, scale) and the AI identifies every line item, calculates quantities, and applies UK construction rates from its 519-item cost library. The result is a professional, itemised quote you can review and send to your client.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I use voice instead of typing?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool works in any modern browser, which means you can use your device's built-in voice-to-text (dictation) to speak your description directly into the text field. Dictate your scope notes on site, paste them in, and the AI turns them into a priced quote. There is no separate voice app to install; use the dictation feature on your phone, tablet, or laptop.",
      },
    },
    {
      "@type": "Question",
      "name": "How accurate is an AI quote from a description?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Accuracy depends on how much detail you provide in the description. A detailed description specifying the job type, materials, scale, and key elements gives the AI more to work with and produces a more accurate quote. For familiar job types like kitchen fit-outs, loft conversions, or single-storey extensions, a clear description is often enough for a review-ready quote. The Proper Tool's validation pipeline also flags any scope gaps or pricing outliers it detects, so you can review before sending.",
      },
    },
    {
      "@type": "Question",
      "name": "Is it free to try?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "Can AI write a construction quote from a description?",
    a: "Yes. The Proper Tool's AI reads your plain-English description of the job and turns it into a fully-priced, itemised construction quote. You describe the scope (trades, elements, scale) and the AI identifies every line item, calculates quantities, and applies UK construction rates from its 519-item cost library. The result is a professional, itemised quote you can review and send to your client.",
  },
  {
    q: "Can I use voice instead of typing?",
    a: "Yes. The Proper Tool works in any modern browser, which means you can use your device's built-in voice-to-text (dictation) to speak your description directly into the text field. Dictate your scope notes on site, paste them in, and the AI turns them into a priced quote. There is no separate voice app to install; use the dictation feature on your phone, tablet, or laptop.",
  },
  {
    q: "How accurate is an AI quote from a description?",
    a: "Accuracy depends on how much detail you provide in the description. A detailed description specifying the job type, materials, scale, and key elements gives the AI more to work with and produces a more accurate quote. For familiar job types like kitchen fit-outs, loft conversions, or single-storey extensions, a clear description is often enough for a review-ready quote. The Proper Tool's validation pipeline also flags any scope gaps or pricing outliers it detects, so you can review before sending.",
  },
  {
    q: "Is it free to try?",
    a: "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
  },
];

const RELATED_PAGES = [
  { href: "/ai-quoting-software/", label: "AI Quoting Software" },
  { href: "/quote-from-drawings/", label: "Quote from Drawings" },
  { href: "/quote-from-photo/", label: "Quote from Photo" },
  { href: "/auto-quote/", label: "Auto Quote" },
  { href: "/ai-takeoff-software/", label: "AI Takeoff Software" },
  { href: "/ai-construction-estimating-software/", label: "AI Construction Estimating" },
  { href: "/quoting-software-for-builders/", label: "Quoting Software for Builders" },
  { href: "/pricing/", label: "Pricing" },
  { href: "/free-trial/", label: "Free Trial" },
];

export default function QuoteFromDescriptionPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Quote from a Description | AI Quote Generator for UK Builders | The Proper Tool"
        description="Describe the job in plain English, by typing or using your voice, and get a fully-priced construction quote in minutes. Built for UK builders."
        canonical="/quote-from-description"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center flex-wrap">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/ai-quoting-software" className="hover:text-foreground transition-colors">AI Quoting Software</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Quote from a Description</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Describe the Job. Get a Priced Quote.
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Type a job description or dictate it on-site. The Proper Tool's AI reads your scope, builds the quote line by line, and prices it to UK rates. No forms, no spreadsheets.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/pricing/">See pricing</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required</p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">How it works</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Three steps from description to a quote ready to send.
            </p>
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              {[
                {
                  step: "1",
                  title: "Type or speak your description",
                  desc: "Describe the job in plain English, or use your phone or laptop's dictation feature to speak it. No specific format needed. For example: \"Knock through the kitchen wall, fit an RSJ, make good both sides, new plasterboard and skim.\"",
                },
                {
                  step: "2",
                  title: "AI interprets the scope",
                  desc: "The AI reads your description and identifies every trade and element: structural work, making good, plastering, skip hire. It builds the scope line by line, estimating quantities from the context you've provided.",
                },
                {
                  step: "3",
                  title: "Fully-priced, itemised quote",
                  desc: "Every line item is priced using the 519-item UK cost library: steel beam supply and fix, labour for making good, plastering rates, waste disposal. The result is a complete, itemised quote you can review, edit, and export.",
                },
              ].map((item) => (
                <div key={item.step} className="relative">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-border bg-muted/30 p-6">
              <p className="text-sm font-semibold mb-2 text-foreground">Example description → itemised output</p>
              <p className="text-sm text-muted-foreground italic mb-4">"Knock through a kitchen wall, fit an RSJ, make good"</p>
              <ul className="space-y-1.5">
                {[
                  "Structural opening: strip, propping, and demolition of masonry wall",
                  "RSJ (universal beam): supply and install including padstones and bearing plates",
                  "Making good: both faces of opening, plasterboard and skim",
                  "Decoration: new plasterwork primed and painted",
                  "Skip hire: debris removal",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* No forms, no spreadsheets */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">No forms, no spreadsheets: just describe it</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Most quoting tools make you fill in long forms: trade by trade, item by item, category by category. If you already know what everything costs and how to describe it, you don't need software; you'd just type it yourself.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              The Proper Tool works the other way round. You describe the job the way you'd describe it to someone on site, in plain English, and the AI does the rest. It works out the scope, builds the line items, and prices them. You review and adjust, not build from scratch.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Mic, label: "Describe in plain English, no specific format required" },
                { icon: FileText, label: "AI builds the full scope from your words" },
                { icon: CheckCircle2, label: "No forms, no dropdowns, no item-by-item entry" },
                { icon: PoundSterling, label: "UK rates applied automatically to every line" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-3 bg-background rounded-lg border border-border p-4">
                  <Icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* From site notes to sent quote */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">From site notes to a sent quote</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              This is the practical use case most builders actually want: you're on site, the client wants a quote, you jot down what needs doing in your notes app or voice memo, then paste it into The Proper Tool when you get back to the van or the office.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              The AI turns your rough site notes into a structured, priced scope. You review, tighten up any numbers that need adjusting, and send the PDF. The whole process, from notes to client, takes under 10 minutes.
            </p>
            <ul className="space-y-3">
              {[
                "Jot notes on site: voice memo, notes app, whatever you use",
                "Paste the text into The Proper Tool",
                "AI interprets the scope and generates a fully-priced quote",
                "Review, adjust any lines, export as a branded PDF",
                "Send to client: done",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Built on UK rates */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Built on UK rates</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Every quote generated from a description is priced from a 519-item UK construction cost library, built for residential construction, covering all 31 common trade categories. Not generic AI rates pulled from the internet: real UK market rates for real UK building work.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              The Pricing Accuracy Index tracks which quotes you accept and adjust, and feeds those signals back into future quotes so they get closer to your numbers over time. Your description quoting gets more accurate the more you use it.
            </p>
            <Link href="/ai-construction-estimating-software/" className="text-primary font-medium hover:underline flex items-center gap-1">
              <ChevronRight className="h-4 w-4" />
              AI construction estimating software
            </Link>
          </div>
        </section>

        {/* When to use description vs drawings */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">When to use a description vs drawings or photos</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              All three input methods work. Which to use depends on what you have and how precise you need the quote to be.
            </p>
            <div className="space-y-4 mb-8">
              <div className="bg-background rounded-xl border border-border p-6">
                <h3 className="font-semibold text-base mb-2">Use a description when…</h3>
                <ul className="space-y-2">
                  {[
                    "It's a familiar job type you've done before: loft conversion, extension, kitchen fit-out",
                    "You're on site and need a quick quote without drawings to hand",
                    "The scope is clear enough to describe clearly in plain English",
                    "Speed matters more than precision at this stage",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-background rounded-xl border border-border p-6">
                <h3 className="font-semibold text-base mb-2">Use drawings for more precision</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Structural work, new builds, and complex projects benefit from drawings; the AI can extract beam sizes, spans, and dimensions directly rather than estimating from your description.
                </p>
                <Link href="/quote-from-drawings/" className="text-sm text-primary font-medium hover:underline flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  Quote from drawings
                </Link>
              </div>
              <div className="bg-background rounded-xl border border-border p-6">
                <h3 className="font-semibold text-base mb-2">Use photos when on site without drawings</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Photos of existing conditions, materials, and the site help the AI understand scope for refurbs and fit-outs where no drawings exist.
                </p>
                <Link href="/quote-from-photo/" className="text-sm text-primary font-medium hover:underline flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  Quote from photo
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {RELATED_PAGES.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Describe the job. Get a priced quote.</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your free 14-day trial. 5 total quote creations across Auto, Template, and Manual quotes included. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
            <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
